import {
  Card, Descriptions, Empty, Space, Spin, Typography,
} from 'antd';
import rentalApi from 'api/rentalApi';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useState } from 'react';
import {
  DamageSeverityTag, DamageStatusTag, formatDateTime, formatMoney, getRentalErrorMessage, OrderStatusTag,
} from '../../rentalConstants';

const { Text, Title } = Typography;

function RentalOrderDetailScreen() {
  const router = useRouter();
  const rentalOrderId = router.query?.rentalOrderId;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!rentalOrderId) return;
    setLoading(true);
    try {
      const response = await rentalApi.getOrder(rentalOrderId);
      setDetail(response?.order || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [rentalOrderId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div>
        {UIHelper.buildBreadcrumb([
          { title: 'Đơn thuê xe', onPress: () => router.navigate(-1) },
          { title: detail?.orderCode || `Đơn #${rentalOrderId}` },
        ])}
      </div>
      <div className="container-fluid px-4 my-4">
        <Card>
          <Spin spinning={loading}>
            {!detail && !loading ? <Empty description="Không tải được đơn" /> : detail && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <Title level={4} className="mb-0">{detail.orderCode}</Title>
                  <OrderStatusTag status={detail.status} />
                </div>

                <Descriptions title="Khách thuê & xe" bordered column={1} size="small">
                  <Descriptions.Item label="Khách thuê">{detail.customer?.fullname} · {detail.customer?.phone}</Descriptions.Item>
                  <Descriptions.Item label="Xe">{detail.vehicle?.vehicleCode} · {detail.vehicle?.plate} · {detail.vehicle?.model}</Descriptions.Item>
                  <Descriptions.Item label="Thời gian thuê">{formatDateTime(detail.startAt)} → {formatDateTime(detail.endAt)}</Descriptions.Item>
                </Descriptions>

                <Descriptions title="Thanh toán" bordered column={1} size="small">
                  <Descriptions.Item label="Đơn giá/ngày">{formatMoney(detail.dailyPrice)}</Descriptions.Item>
                  <Descriptions.Item label="Tiền thuê gốc">{formatMoney(detail.baseFare)}</Descriptions.Item>
                  <Descriptions.Item label="Phí trễ giờ">{formatMoney(detail.lateFee)}</Descriptions.Item>
                  <Descriptions.Item label="Tổng tiền">{formatMoney(detail.totalFare)}</Descriptions.Item>
                  <Descriptions.Item label="Hình thức">{detail.paymentMethod}</Descriptions.Item>
                  <Descriptions.Item label="Trạng thái thanh toán">{detail.paymentStatus}</Descriptions.Item>
                </Descriptions>

                {(detail.status === 'CANCELLED' || detail.status === 'REJECTED') && (
                  <Descriptions title="Huỷ / Từ chối" bordered column={1} size="small">
                    <Descriptions.Item label="Bởi">{detail.cancelledBy || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Lý do">{detail.cancelReason || detail.rejectReason || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Lúc">{formatDateTime(detail.cancelledAt)}</Descriptions.Item>
                  </Descriptions>
                )}

                {detail.handoverAt && (
                  <Descriptions title="Giao xe" bordered column={1} size="small">
                    <Descriptions.Item label="Lúc">{formatDateTime(detail.handoverAt)}</Descriptions.Item>
                    <Descriptions.Item label="Odo">{detail.handoverOdometerKm} km</Descriptions.Item>
                    <Descriptions.Item label="Pin">{detail.handoverBatteryPercent}%</Descriptions.Item>
                  </Descriptions>
                )}

                {detail.returnAt && (
                  <Descriptions title="Trả xe" bordered column={1} size="small">
                    <Descriptions.Item label="Lúc">{formatDateTime(detail.returnAt)}</Descriptions.Item>
                    <Descriptions.Item label="Odo">{detail.returnOdometerKm} km</Descriptions.Item>
                    <Descriptions.Item label="Pin">{detail.returnBatteryPercent}%</Descriptions.Item>
                  </Descriptions>
                )}

                {(detail.damageReports || []).length > 0 && (
                  <div>
                    <Title level={5}>Báo cáo hư hỏng</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {detail.damageReports.map((report) => (
                        <Card key={report.rentalDamageReportId} size="small">
                          <div className="d-flex justify-content-between align-items-center gap-3">
                            <div>
                              <DamageSeverityTag severity={report.severity} />
                              {' '}
                              <Text>{report.description || 'Không có mô tả'}</Text>
                            </div>
                            <DamageStatusTag status={report.status} />
                          </div>
                        </Card>
                      ))}
                    </Space>
                  </div>
                )}
              </Space>
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
}

export default RentalOrderDetailScreen;
